import { WEBHOOKS } from '../context/AppContext';
import { APP_VARIANT } from '../config/appVariant';

// ponytail: per-page-load cap stops a render loop from flooding n8n; add server-side dedupe if volume grows.
const MAX_REPORTS = 20;
let sent = 0;
let enabled = false;
// The same Error often passes through several layers (postWebhook, then a
// catch block's console.error, then a toast); report each object only once.
const reported = new WeakSet();
const originalFetch = window.fetch.bind(window);

// For callers that report their own failures (utils/webhook.js), so the fetch
// hook below doesn't add a second, less detailed row for the same request.
export const rawFetch = (...args) => originalFetch(...args);

const currentUserId = () => {
  try {
    return JSON.parse(localStorage.getItem('currentUser'))?.id || null;
  } catch {
    return null;
  }
};

const describe = (value) => {
  // `details` carries structured context such as WebhookError's url/status/serverMessage.
  if (value instanceof Error) return { message: value.message, stack: value.stack, ...value.details };
  if (typeof value === 'string') return { message: value };
  try {
    return { message: JSON.stringify(value) };
  } catch {
    return { message: String(value) };
  }
};

// Fire-and-forget POST to n8n. Must never throw or call console.error,
// since console.error itself is routed here.
export const logError = (source, error, extra = {}) => {
  if (!enabled) return;
  if (error && typeof error === 'object') {
    if (reported.has(error)) return;
    reported.add(error);
  }
  if (sent >= MAX_REPORTS) return;
  sent += 1;
  const { message, stack, ...details } = describe(error);
  try {
    originalFetch(WEBHOOKS.LOG_ERROR, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        source,
        message: String(message).slice(0, 2000),
        stack: stack ? String(stack).slice(0, 4000) : null,
        userId: currentUserId(),
        appVariant: APP_VARIANT,
        language: localStorage.getItem('language') || 'en',
        route: window.location.pathname,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ...details,
        ...extra
      })
    }).catch(() => {});
  } catch {
    // Reporting is best effort.
  }
};

export const installErrorLogging = () => {
  enabled = true;
  window.addEventListener('error', (e) => logError('window.onerror', e.error || e.message));
  window.addEventListener('unhandledrejection', (e) => logError('unhandledrejection', e.reason));

  // The existing catch blocks already console.error with a label ("Login error:", …).
  const originalConsoleError = console.error;
  console.error = (...args) => {
    originalConsoleError(...args);
    const error = args.find((a) => a instanceof Error);
    const label = args.filter((a) => typeof a === 'string').join(' ');
    logError('console.error', error || label || args[0], { label });
  };

  // Every backend call is an n8n webhook; log the failing URL, which the catch blocks don't carry.
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input?.url;
    const isWebhook = typeof url === 'string' && url.includes('/webhook/') && url !== WEBHOOKS.LOG_ERROR;
    try {
      const response = await originalFetch(input, init);
      if (isWebhook && !response.ok) logError('fetch', `HTTP ${response.status}`, { webhook: url, status: response.status });
      return response;
    } catch (err) {
      if (isWebhook && err?.name !== 'AbortError') logError('fetch', err, { webhook: url });
      throw err;
    }
  };
};
